import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Avatar } from '@lib/ui';

export type ChatBubbleProps = {
  children: ReactNode;
  variant: 'assistant' | 'user';
  avatar?: string;
  animate?: boolean;
  className?: string;
};

function ChatBubble({ children, variant, avatar, animate = false, className }: ChatBubbleProps) {
  const isAssistant = variant === 'assistant';

  return (
    <div
      className={clsx(
        'flex gap-3',
        isAssistant ? 'flex-row' : 'flex-row-reverse',
        animate && 'animate-in fade-in slide-in-from-bottom-2 duration-300',
        className
      )}
    >
      {/* Avatar */}
      <Avatar
        size="sm"
        className={clsx(
          'shrink-0',
          isAssistant ? 'bg-primary-500 text-white' : 'bg-gray-500 text-white dark:bg-dark-400'
        )}
        name={avatar}
      />

      {/* Message bubble */}
      <div
        className={clsx(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isAssistant
            ? [
                'bg-white dark:bg-dark-700',
                'border border-gray-200 dark:border-dark-600',
                'rounded-tl-md',
              ]
            : ['bg-primary-500 text-white', 'rounded-tr-md']
        )}
      >
        <div
          className={clsx(
            'text-sm',
            isAssistant ? 'text-gray-700 dark:text-dark-200' : 'text-white'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

ChatBubble.displayName = 'ChatBubble';

export { ChatBubble };
